import { useCallback, useEffect, useRef } from 'react'
import { ApiError } from '../data/errors'

// Ações otimistas com janela de "Desfazer" + fila offline. Tiradas de dentro do
// App.tsx, onde a janela era decidida por `process.env.VITEST` (o caminho de 5s nunca
// rodava em teste) e a fila vivia num ref sem dono.
export const DEFAULT_UNDO_WINDOW_MS = 5000
export const DEFAULT_OFFLINE_RETRY_MS = 15000

export type Notify = (
  message: string,
  tone?: string,
  code?: string | null,
  action?: { label: string; fn: () => void },
) => void

export interface PendingDebounce {
  timeoutId: ReturnType<typeof setTimeout>
  onRevert: () => void
  onCommit: () => Promise<void>
  /** Commit com as mesmas semânticas do disparo do timer (offline fila, erro reverte). */
  run: () => Promise<void>
}

interface QueuedAction {
  key: string
  onRevert: () => void
  onCommit: () => Promise<void>
  toastMsg: string
}

// Fila offline persistente entre montagens: o DashboardApp remonta por key no
// logout/troca de unidade; se a fila vivesse num ref, as ações do usuário morriam
// com o componente. Closures de set de componente morto viram no-ops inofensivos.
const offlineQueue: QueuedAction[] = []
/** Reset p/ testes (a fila é módulo-level por design). */
export function __resetOfflineQueueForTests(): void {
  offlineQueue.length = 0
}

export interface UseDebouncedActionsDeps {
  /** Toast da tela (o mesmo usado em todo o App). */
  notify: Notify
  /** Janela de desfazer em ms. 0 = commita na hora, sem desfazer. */
  windowMs?: number
  /** Intervalo de retentativa da fila offline em ms. */
  retryMs?: number
  /** Estado de conexão; injetável para teste. */
  isOnline?: () => boolean
}

export interface DebouncedActions {
  register: (
    key: string,
    onRevert: () => void,
    onCommit: () => Promise<void>,
    toastMsg: string,
    toastTone?: string,
    toastCode?: string | null,
    toastUndoLabel?: string,
  ) => void
  /** Chaves com commit pendente — o realtime usa para não recarregar por eco. */
  pendingKeys: () => Iterable<string>
  /** Tenta esvaziar a fila offline agora. */
  flush: () => Promise<void>
  /** Commita AGORA a ação pendente da chave (pula a janela de Desfazer). No-op se não há pendência. */
  flushKey: (key: string) => Promise<void>
}

/** Falha de rede NÃO é ApiError>0: rede cai no meio do commit como ApiError status 0
 *  (o ds mapeia fetch-fail p/ ApiError 0) ou como Error puro. Ela volta pra fila —
 *  contradiz o propósito da fila online/offline (audit ux-realtime #1/#5). */
export function isNetworkError(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status === 0
}

export function useDebouncedActions(deps: UseDebouncedActionsDeps): DebouncedActions {
  const { notify } = deps
  const windowMs = deps.windowMs ?? DEFAULT_UNDO_WINDOW_MS
  const retryMs = deps.retryMs ?? DEFAULT_OFFLINE_RETRY_MS
  const onlineRef = useRef(deps.isOnline)
  onlineRef.current = deps.isOnline
  const isOnline = useCallback(
    () => (onlineRef.current ? onlineRef.current() : typeof navigator === 'undefined' || navigator.onLine),
    [],
  )

  const timersRef = useRef<Map<string, PendingDebounce>>(new Map())

  const flush = useCallback(async () => {
    if (offlineQueue.length === 0) {return}
    if (!isOnline()) {return}

    const queue = [...offlineQueue]
    offlineQueue.length = 0

    notify(`Sincronizando ${queue.length} ações offline pendentes…`, 'warning')

    for (const action of queue) {
      try {
        await action.onCommit()
        notify(`Sincronizado: ${action.toastMsg}`, 'success')
      } catch (err) {
        if (isNetworkError(err)) {
          // Continua na fila: erro de rede não é culpa da ação.
          offlineQueue.push(action)
        } else {
          action.onRevert()
          notify(`Falha ao sincronizar: ${action.toastMsg}. ${err instanceof ApiError ? err.message : 'erro'}`, 'error')
        }
      }
    }
  }, [isOnline, notify])

  const register = useCallback<DebouncedActions['register']>((
    key, onRevert, onCommit, toastMsg, toastTone = 'success', toastCode = null, toastUndoLabel = 'Desfazer',
  ) => {
    // Mesma chave de novo: a ação anterior não pode esperar mais.
    const existing = timersRef.current.get(key)
    if (existing) {
      clearTimeout(existing.timeoutId)
      void existing.onCommit()
      timersRef.current.delete(key)
    }

    const runCommit = async () => {
      timersRef.current.delete(key)

      if (!isOnline()) {
        offlineQueue.push({ key, onCommit, onRevert, toastMsg })
        notify(`Desconectado. Sincronizando após reconexão: ${toastMsg}`, 'warning')
        return
      }

      try {
        await onCommit()
      } catch (err) {
        if (isNetworkError(err)) {
          offlineQueue.push({ key, onCommit, onRevert, toastMsg })
          notify(`Falha de conexão. Sincronizando após reconexão: ${toastMsg}`, 'warning')
        } else {
          // Erro de negócio: o servidor recusou, a tela volta ao estado anterior —
          // e o operador vê A MENSAGEM do backend, não só o 'desfeito' do otimismo.
          onRevert()
          notify(`Falha ao confirmar: ${err instanceof ApiError ? err.message : 'erro inesperado'}`, 'error')
        }
      }
    }

    if (windowMs <= 0) {
      void runCommit()
      notify(toastMsg, toastTone, toastCode)
      return
    }

    const timeoutId = setTimeout(runCommit, windowMs)
    timersRef.current.set(key, { timeoutId, onRevert, onCommit, run: runCommit })

    notify(toastMsg, toastTone, toastCode, {
      label: toastUndoLabel,
      fn: () => {
        const item = timersRef.current.get(key)
        if (item) {
          clearTimeout(item.timeoutId)
          item.onRevert()
          timersRef.current.delete(key)
        }
      },
    })
  }, [isOnline, notify, windowMs])

  const pendingKeys = useCallback(() => timersRef.current.keys(), [])

  // Commit imediato da pendência (reopen→confirm): mesmas semânticas do timer —
  // offline enfileira, erro de negócio reverte e notifica. No-op sem pendência.
  const flushKey = useCallback((key: string): Promise<void> => {
    const item = timersRef.current.get(key)
    if (!item) {return Promise.resolve()}
    clearTimeout(item.timeoutId)
    return item.run()
  }, [])

  // Reconexão e retentativa periódica da fila.
  useEffect(() => {
    if (typeof window === 'undefined') {return}
    const handleOnline = () => { void flush() }
    window.addEventListener('online', handleOnline)
    const interval = setInterval(() => {
      if (offlineQueue.length > 0) { void flush() }
    }, retryMs)
    return () => {
      window.removeEventListener('online', handleOnline)
      clearInterval(interval)
    }
  }, [flush, retryMs])

  // Fila pode ter chegado cheia de uma sessão anterior (logout/remount): tenta já.
  // Mount-only: flush vazio da sessão anterior não deve rodar a cada notify re-render.
  const flushedOnce = useRef(false)
  useEffect(() => {
    if (flushedOnce.current) {return}
    flushedOnce.current = true
    if (offlineQueue.length > 0) { void flush() }
  }, [flush])

  // Ao desmontar, a ação pendente é enviada em vez de sumir: o usuário já viu o toast
  // de sucesso otimista, então perder a chamada seria pior que disparar sem tela.
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((item) => {
        clearTimeout(item.timeoutId)
        void item.onCommit()
      })
      timers.clear()
    }
  }, [])

  return { register, pendingKeys, flush, flushKey }
}
